
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { MessageSquare, Users } from 'lucide-react';

interface Conversation {
    id: string;
    type: 'dm' | 'group';
    name?: string;
    updated_at: string;
    last_message_at: string;
    last_message_content?: string;
    last_message_sender_id?: string;
    participants: {
        user_id: string;
        user: {
            name: string;
            avatar?: string;
        };
    }[];
    unread_count: number;
}

interface ConversationListProps {
    onSelect: (id: string) => void;
    selectedId: string | null;
    searchQuery?: string;
}

const ConversationList: React.FC<ConversationListProps> = ({ onSelect, selectedId, searchQuery = '' }) => {
    const { user } = useAuth();
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user) {
            fetchConversations();

            // Subscribe to both messages and participants (for read status)
            const subscriptions = [
                subscribeToMessages(),
                subscribeToParticipants()
            ];

            return () => {
                subscriptions.forEach(unsub => unsub());
            };
        }
    }, [user]);

    const fetchConversations = async () => {
        try {
            const { data, error } = await supabase.rpc('get_conversations_with_unread');

            if (error) throw error;

            console.log('Fetched conversations:', data);
            setConversations(data as any);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const subscribeToMessages = () => {
        const subscription = supabase
            .channel('public:chat_messages:conversations_list')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'chat_messages'
            }, () => {
                // When a new message arrives, re-fetch to update content, time, and unread count
                fetchConversations();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    };

    const subscribeToParticipants = () => {
        // Listen for updates to last_read_at to clear unread counts
        const subscription = supabase
            .channel('public:chat_participants:conversations_list')
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'chat_participants',
                filter: `user_id=eq.${user?.id}`
            }, () => {
                fetchConversations();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    };

    const getConversationName = (conv: Conversation) => {
        if (conv.type === 'group') {
            return conv.name || 'İsimsiz Grup';
        }
        // For DM, find the other participant
        const otherParticipant = conv.participants?.find(p => p.user_id !== user?.id);
        return otherParticipant?.user?.name || 'Bilinmeyen Kullanıcı';
    };

    const getConversationIcon = (conv: Conversation) => {
        if (conv.type === 'group') {
            return (
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0">
                    <Users className="w-5 h-5" />
                </div>
            );
        }
        const otherParticipant = conv.participants?.find(p => p.user_id !== user?.id);
        if (otherParticipant?.user?.avatar) {
            return <img src={otherParticipant.user.avatar} alt="Avatar" className="w-10 h-10 rounded-full object-cover shrink-0" />
        }
        return (
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 shrink-0">
                <span className="font-semibold">{getConversationName(conv).charAt(0)}</span>
            </div>
        );
    }

    const filteredConversations = conversations.filter(conv =>
        getConversationName(conv).toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return <div className="p-4 text-center text-gray-500">Yükleniyor...</div>;
    }

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
                {filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full p-4 text-center text-gray-500">
                        <MessageSquare className="w-12 h-12 mb-2 text-gray-300" />
                        <p className="font-medium">Henüz mesajınız yok.</p>
                        <p className="text-sm mt-1">"Yeni Sohbet" butonunu kullanarak bir görüşme başlatın.</p>
                    </div>
                ) : (
                    filteredConversations.map(conv => (
                        <div
                            key={conv.id}
                            onClick={() => {
                                onSelect(conv.id);
                                // Optimistically clear unread count
                                setConversations(prev => prev.map(c =>
                                    c.id === conv.id ? { ...c, unread_count: 0 } : c
                                ));
                            }}
                            className={`p-4 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800 ${selectedId === conv.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                }`}
                        >
                            {getConversationIcon(conv)}
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-baseline mb-1">
                                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                        {getConversationName(conv)}
                                    </h3>
                                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                                        {new Date(conv.last_message_at).toLocaleDateString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate pr-2">
                                        {conv.last_message_content ? (
                                            <span className="truncate">
                                                {conv.last_message_sender_id === user?.id && <span className="font-bold mr-1">Siz:</span>}
                                                {conv.last_message_content}
                                            </span>
                                        ) : (
                                            conv.type === 'group' ? 'Grup sohbeti' : 'Mesaj gönder'
                                        )}
                                    </p>
                                    {conv.unread_count > 0 && selectedId !== conv.id && (
                                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center shrink-0">
                                            {conv.unread_count}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ConversationList;
